import { TrendingUp, Building2, Users, FileText, CheckCircle2, Target } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function Services() {
  const services = [
    {
      icon: <TrendingUp size={48} />,
      title: 'Real Estate Consulting',
      description: 'Comprehensive advisory services for property investment, market analysis, and portfolio optimization.',
      features: [
        'Market trend analysis and forecasting',
        'Property valuation and due diligence',
        'Investment strategy development',
        'Risk assessment and mitigation',
        'Portfolio performance optimization'
      ]
    },
    {
      icon: <Building2 size={48} />,
      title: 'Project Development',
      description: 'End-to-end project management from initial concept through successful completion and delivery.',
      features: [
        'Feasibility studies and site analysis',
        'Design coordination and approvals',
        'Construction management oversight',
        'Budget and timeline management',
        'Quality assurance and delivery'
      ]
    },
    {
      icon: <FileText size={48} />,
      title: 'Viability Analysis',
      description: 'In-depth project evaluation to determine financial feasibility and potential return on investment.',
      features: [
        'Financial modeling and projections',
        'Market demand assessment',
        'Competitive analysis',
        'Risk and opportunity evaluation',
        'Investment recommendations'
      ]
    },
    {
      icon: <Users size={48} />,
      title: 'Investor Linking Services',
      description: 'Connect with strategic investment partners for projects requiring complex funding arrangements.',
      features: [
        'Access to extensive investor network',
        'Strategic partnership facilitation',
        'Deal structuring and negotiation support',
        'Multi-source funding coordination',
        'Relationship management'
      ]
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Initial Consultation',
      description: 'We begin by understanding your goals, timeline, and investment criteria through a comprehensive consultation.'
    },
    {
      step: '02',
      title: 'Analysis & Strategy',
      description: 'Our team conducts thorough market research and develops a tailored strategy aligned with your objectives.'
    },
    {
      step: '03',
      title: 'Implementation',
      description: 'We execute the plan with precision, managing all aspects of the project and keeping you informed every step.'
    },
    {
      step: '04',
      title: 'Optimization & Support',
      description: 'Post-completion, we provide ongoing support and optimization to ensure sustained success and value creation.'
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="services-page">
      <section className="py-24 md:py-32 bg-[#020617]" data-testid="services-hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h1 className="text-5xl md:text-7xl font-medium tracking-tight leading-none text-[#f8fafc] mb-8" data-testid="services-title">
            Our <span className="text-[#d4af37]">Services</span>
          </h1>
          <p className="text-lg md:text-xl leading-relaxed text-slate-300 max-w-3xl mx-auto">
            Comprehensive real estate investment and consulting services tailored to your unique needs and objectives
          </p>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#0f172a]" data-testid="services-detail-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="space-y-16">
            {services.map((service, index) => (
              <div
                key={index}
                data-testid={`service-detail-${index}`}
                className="glass-morphism p-8 md:p-12 hover:border-[#d4af37]/30 transition-all duration-500"
              >
                <div className="flex items-start space-x-6 mb-8">
                  <div className="text-[#d4af37] flex-shrink-0">
                    {service.icon}
                  </div>
                  <div>
                    <h2 className="text-3xl md:text-4xl font-normal text-[#f8fafc] mb-4">
                      {service.title}
                    </h2>
                    <p className="text-lg leading-relaxed text-slate-300">
                      {service.description}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-0 md:ml-20">
                  {service.features.map((feature, fIndex) => (
                    <div key={fIndex} className="flex items-start space-x-3" data-testid={`feature-${index}-${fIndex}`}>
                      <CheckCircle2 className="text-[#d4af37] flex-shrink-0 mt-1" size={20} />
                      <span className="text-base text-slate-400">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#020617]" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-normal tracking-tight text-[#f8fafc] mb-6">
              Our Process
            </h2>
            <p className="text-lg md:text-xl leading-relaxed text-slate-300 max-w-3xl mx-auto">
              A proven methodology refined over three decades of successful project delivery
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((item, index) => (
              <div
                key={index}
                data-testid={`process-step-${index}`}
                className="relative"
              >
                <div className="bg-slate-900/50 border border-white/5 p-8 hover:border-[#d4af37]/30 transition-all duration-500 h-full">
                  <div className="text-6xl font-medium text-[#d4af37]/20 mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-2xl font-normal text-[#f8fafc] mb-4">
                    {item.title}
                  </h3>
                  <p className="text-base leading-relaxed text-slate-400">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#0f172a]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <Target className="text-[#d4af37] mx-auto mb-8" size={64} />
          <h2 className="text-4xl md:text-5xl font-normal tracking-tight text-[#f8fafc] mb-8">
            Ready to Start Your Project?
          </h2>
          <p className="text-lg md:text-xl leading-relaxed text-slate-300 mb-12">
            Schedule a consultation with our experienced team to discuss your real estate investment goals and discover how we can help you succeed.
          </p>
          <Link to="/contact">
            <Button className="bg-[#d4af37] text-[#020617] hover:bg-[#b5952f] rounded-sm px-8 py-6 text-lg font-medium transition-all duration-300 hover:scale-105" data-testid="schedule-consultation-button">
              Schedule Consultation
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}